import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Send, LayoutTemplate, Image as ImageIcon,
  Loader as Loader2, Eye, Calendar as CalendarIcon,
  ChevronRight, Pen, Trash2, Settings2, Copy, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProposalBlockEditor } from '@/components/proposals/ProposalBlockEditor';

import { GradientEditor, type GradientSettings } from '@/components/brand/ui/GradientEditor';
import { MERGE_TAG_DEFS } from '@/components/proposals/mergeTags';
import { FooterVariableTextarea } from '@/components/proposals/FooterVariableTextarea';
import { proposalService, parseCoverData, type Proposal, type TitleSettings } from '@/lib/proposalService';
import { projectService } from '@/lib/projectService';
import { useWorkspace } from '@/context/WorkspaceContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { TextToolbar } from '@/components/brand/ui/TextToolbar';

export default function ProposalEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();

  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [title, setTitle] = useState('');
  const [coverPhoto, setCoverPhoto] = useState('');
  const [coverColor, setCoverColor] = useState('#E8E4DC');
  const [footerBgColor, setFooterBgColor] = useState('#ffffff');
  const [footerBgType, setFooterBgType] = useState<'solid' | 'gradient'>('solid');
  const [footerGradient, setFooterGradient] = useState('');
  const [footerGradientSettings, setFooterGradientSettings] = useState<GradientSettings>({
    type: 'linear', angle: 135,
    stops: [{ color: '#667eea', position: 0 }, { color: '#764ba2', position: 100 }]
  });
  const [footerOpacity, setFooterOpacity] = useState(1);
  const [footerStyleSettings, setFooterStyleSettings] = useState({
    titleColor: '#111111', titleSize: 20, textColor: '#333333', textSize: 14
  });
  const [footerSettings, setFooterSettings] = useState({
    leftTitle: 'CREATED BY:',
    rightTitle: 'PREPARED FOR:',
    leftContent: '[Sender.FirstName] [Sender.LastName]\n[Sender.Company]',
    rightContent: '[Client.FirstName] [Client.LastName]\n[Client.Company]',
  });
  const [coverBgType, setCoverBgType] = useState<'solid' | 'gradient'>('solid');
  const [coverGradient, setCoverGradient] = useState('linear-gradient(135deg, #FF9A9E 0%, #FECFEF 100%)');
  const [coverGradientSettings, setCoverGradientSettings] = useState<GradientSettings>({
    type: 'linear',
    angle: 135,
    stops: [
      { color: '#FF9A9E', position: 0 },
      { color: '#FECFEF', position: 100 }
    ]
  });
  const [titleSettings, setTitleSettings] = useState<TitleSettings>({
    fontSize: 72,
    letterSpacing: -0.03,
    textCase: 'capitalize',
    color: '#111827',
    isBold: true,
  });
  const [agencyLogoOverride, setAgencyLogoOverride] = useState('');
  const [content, setContent] = useState<any[]>([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // Send dialog state
  const [clientId, setClientId] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [validUntil, setValidUntil] = useState<Date | undefined>();

  const saveTimer = useRef<number | null>(null);
  const dirtyRef = useRef(false);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const titleTextareaRef = useRef<HTMLTextAreaElement>(null);

  const [showTitleToolbar, setShowTitleToolbar] = useState(false);
  const [titleToolbarPos, setTitleToolbarPos] = useState({ top: 0, left: 0 });
  const titleWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showTitleToolbar && titleWrapperRef.current) {
      const rect = titleWrapperRef.current.getBoundingClientRect();
      setTitleToolbarPos({ top: rect.top, left: rect.left });
    }
  }, [showTitleToolbar, title]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showTitleToolbar && titleWrapperRef.current && !titleWrapperRef.current.contains(e.target as Node)) {
        setShowTitleToolbar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTitleToolbar]);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const p = await proposalService.getProposal(id);
      if (!p) { toast.error('Proposal not found'); navigate('/proposals'); return; }
      setProposal(p);
      setTitle(p.title);
      const parsedCover = parseCoverData(p.cover_image_url);
      setCoverPhoto(parsedCover.image);
      setCoverColor(parsedCover.color);
      setCoverBgType(parsedCover.bgType || 'solid');
      setCoverGradient(parsedCover.gradient || 'linear-gradient(135deg, #FF9A9E 0%, #FECFEF 100%)');
      if (parsedCover.gradientSettings) {
        setCoverGradientSettings(parsedCover.gradientSettings);
      }
      setFooterBgColor(parsedCover.footerBgColor || '#ffffff');
      setFooterBgType(parsedCover.footerBgType || 'solid');
      setFooterGradient(parsedCover.footerGradient || '');
      if (parsedCover.footerGradientSettings) setFooterGradientSettings(parsedCover.footerGradientSettings);
      setFooterOpacity(parsedCover.footerOpacity !== undefined ? parsedCover.footerOpacity : 1);
      if (parsedCover.footerSettings) setFooterSettings(parsedCover.footerSettings);
      if (parsedCover.footerStyleSettings) setFooterStyleSettings(parsedCover.footerStyleSettings);
      setTitleSettings(parsedCover.titleSettings);
      setClientId(p.client_user_id || '');
      setClientEmail(p.client_email);
      setClientName(p.client_name);
      setCompanyName(p.company_name);
      setValidUntil(p.valid_until ? new Date(p.valid_until) : undefined);
      setContent(Array.isArray(p.content) ? p.content : []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load proposal');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!currentWorkspace) return;
    projectService.getWorkspaceClients(currentWorkspace.id).then(setClients).catch(() => {});
  }, [currentWorkspace?.id]);

  const markDirty = () => {
    dirtyRef.current = true;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => { void saveDraft(true); }, 2000);
  };

  const saveDraft = useCallback(async (silent = false) => {
    if (!proposal) return;
    if (!dirtyRef.current && silent) return;
    try {
      setSaving(true);
      const updated = await proposalService.updateProposal(proposal.id, {
        title,
        cover_image_url: JSON.stringify({ 
          image: coverPhoto, 
          color: coverColor, 
          bgType: coverBgType, 
          gradient: coverGradient, 
          gradientSettings: coverGradientSettings, 
          footerBgColor, 
          footerBgType,
          footerGradient,
          footerGradientSettings,
          footerOpacity,
          footerSettings,
          footerStyleSettings,
          titleSettings 
        }),
        client_user_id: clientId || null,
        client_email: clientEmail,
        client_name: clientName,
        company_name: companyName,
        valid_until: validUntil ? validUntil.toISOString() : null,
        content,
      });
      setProposal(updated);
      dirtyRef.current = false;
      if (!silent) toast.success('Draft saved');
    } catch (err) {
      console.error(err);
      if (!silent) toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }, [proposal, title, coverPhoto, coverColor, coverBgType, coverGradient, coverGradientSettings, footerBgColor, footerBgType, footerGradient, footerGradientSettings, footerOpacity, footerSettings, footerStyleSettings, titleSettings, clientId, clientEmail, clientName, companyName, validUntil, content]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTitle(e.target.value);
    markDirty();
  };

  useEffect(() => {
    const adjustHeight = () => {
      if (titleTextareaRef.current) {
        titleTextareaRef.current.style.height = 'auto';
        titleTextareaRef.current.style.height = `${titleTextareaRef.current.scrollHeight}px`;
      }
    };
    
    adjustHeight();
    window.addEventListener('resize', adjustHeight);
    return () => window.removeEventListener('resize', adjustHeight);
  }, [title, titleSettings.fontSize, titleSettings.lineHeight, titleSettings.fontFamily]);

  const updateTitleSettings = (updates: Partial<TitleSettings>) => {
    setTitleSettings(prev => ({ ...prev, ...updates }));
    markDirty();
  };

  const titleTextStyles = {
    fontSize: titleSettings.fontSize,
    letterSpacing: `${titleSettings.letterSpacing}em`,
    textTransform: titleSettings.textCase,
    color: titleSettings.color,
    fontWeight: titleSettings.isBold ? 'bold' : 'normal',
    fontStyle: titleSettings.isItalic ? 'italic' : 'normal',
    textDecoration: titleSettings.isUnderline ? 'underline' : 'none',
    textAlign: titleSettings.alignment || 'left',
    lineHeight: titleSettings.lineHeight || 1.05,
    fontFamily: titleSettings.fontFamily || 'Inter',
  };

  const handleTitleStyleChange = (key: string, value: any) => {
    switch (key) {
      case 'fontSize':
        updateTitleSettings({ fontSize: Number(value) });
        break;
      case 'letterSpacing':
        updateTitleSettings({ letterSpacing: parseFloat(value) });
        break;
      case 'textTransform':
        updateTitleSettings({ textCase: value as any });
        break;
      case 'color':
        updateTitleSettings({ color: value });
        break;
      case 'fontWeight':
        updateTitleSettings({ isBold: value === 'bold' || value === 900 || value === 700 });
        break;
      case 'fontStyle':
        updateTitleSettings({ isItalic: value === 'italic' });
        break;
      case 'textDecoration':
        updateTitleSettings({ isUnderline: value === 'underline' });
        break;
      case 'textAlign':
        updateTitleSettings({ alignment: value as any });
        break;
      case 'fontFamily':
        updateTitleSettings({ fontFamily: value });
        break;
      case 'lineHeight':
        updateTitleSettings({ lineHeight: value });
        break;
    }
  };

  const proposalHeadingTypes = [
    { value: 'h1', label: 'H1', size: 72 },
    { value: 'h2', label: 'H2', size: 60 },
    { value: 'h3', label: 'H3', size: 48 },
    { value: 'h4', label: 'H4', size: 36 },
    { value: 'h5', label: 'H5', size: 30 },
    { value: 'h6', label: 'H6', size: 24 }
  ];

  const handleImageUpload = async (
    file: File,
    setter: (url: string) => void,
    folder: string,
  ) => {
    if (!currentWorkspace) return;
    try {
      const ext = file.name.split('.').pop();
      const path = `${currentWorkspace.id}/proposals/${folder}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from('avatars').getPublicUrl(path);
      setter(data.publicUrl);
      markDirty();
    } catch (err) {
      console.error(err);
      toast.error('Upload failed');
    }
  };

  const handleClientSelect = (value: string) => {
    setClientId(value);
    const c = clients.find(c => c.id === value);
    if (c) { setClientName(c.name || ''); setClientEmail(c.email || ''); }
    markDirty();
  };

  const handleSend = async () => {
    if (!proposal) return;
    if (!clientId) { toast.error('Please select a client'); return; }
    if (!validUntil) { toast.error('Please set an expiry date'); return; }
    dirtyRef.current = true; // Force save on send
    await saveDraft(true);
    try {
      await proposalService.sendProposal(proposal.id);
      toast.success('Proposal sent to client');
      setSendOpen(false);
      navigate('/proposals');
    } catch (err) {
      console.error(err);
      toast.error('Failed to send proposal');
    }
  };

  const handleSaveTemplate = async () => {
    if (!proposal || !templateName.trim()) return;
    try {
      await saveDraft(true);
      await proposalService.saveAsTemplate(proposal.id, templateName.trim());
      toast.success('Template saved');
      setTemplateOpen(false);
      setTemplateName('');
    } catch { toast.error('Failed to save template'); }
  };

  // workspace branding
  const appearance = currentWorkspace?.appearance;
  const accentColor = appearance?.theme?.accentColor || '#111827';
  const wsLogo = appearance?.logo || currentWorkspace?.logo;
  const effectiveLogo = agencyLogoOverride || wsLogo;
  const agencyName = currentWorkspace?.name || 'Agency';

  if (loading) return null;

  const isEditable = proposal?.status === 'draft';

  return (
    <div className="min-h-screen bg-transparent flex flex-col">
      <Helmet><title>{title || 'Proposal'} | Editor</title></Helmet>

      {/* ── Full-width top bar ── */}
      <div className="sticky top-0 z-30 bg-white dark:bg-card border-b border-gray-200 dark:border-border shadow-sm w-full">
        <div className="w-full px-6 py-3 flex items-center justify-between gap-3">
          {/* Left: back + breadcrumb */}
          <div className="flex items-center gap-2 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground hover:text-foreground shrink-0"
              onClick={() => navigate('/proposals')}
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline font-normal">Proposals</span>
            </Button>
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 hidden sm:block" />
            <span className="text-sm font-semibold truncate">{title || 'Untitled'}</span>
            <span className={cn(
              'shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full',
              proposal?.status === 'draft'
                ? 'bg-gray-100 dark:bg-muted text-gray-600 dark:text-gray-300'
                : 'bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400',
            )}>
              {proposal?.status === 'draft' ? 'Draft' : proposal?.status}
            </span>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground mr-1" />}
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={() => navigate(`/proposals/${proposal?.id}/preview`)}
            >
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline">Preview</span>
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setTemplateOpen(true)}>
              <LayoutTemplate className="w-4 h-4" />
              <span className="hidden sm:inline">Template</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => saveDraft(false)}
              disabled={saving}
            >
              <Save className="w-4 h-4" />
              <span className="hidden sm:inline">Save</span>
            </Button>
            <Button
              size="sm"
              className="gap-1.5 text-white"
              style={{ backgroundColor: accentColor }}
              onClick={() => setSendOpen(true)}
              disabled={!isEditable}
            >
              <Send className="w-4 h-4" />
              <span>Send to Client</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ── Page canvas ── */}
      <div className="flex-1 flex flex-col items-center py-10 px-6 gap-8">

        {/* Cover page – 9:16 aspect ratio */}
        <div
          className="relative overflow-hidden w-full transition-all"
          style={{ 
            maxWidth: '960px', 
            aspectRatio: '4/5', 
            borderRadius: '4px', 
            background: coverBgType === 'gradient' ? coverGradient : coverColor 
          }}
        >
          {/* Background cover photo (if any) */}
          {coverPhoto && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${coverPhoto})`, opacity: 0.18 }}
            />
          )}

          {/* Top strip: logo area */}
          <div className="relative z-10 flex items-start justify-between px-16 sm:px-20 pt-16 sm:pt-20 pb-0">
            <button
              className="group"
              onClick={() => isEditable && logoInputRef.current?.click()}
              title={isEditable ? 'Click to upload agency logo' : undefined}
            >
              {effectiveLogo ? (
                <img
                  src={effectiveLogo}
                  alt={agencyName}
                  className={cn(
                    'h-20 w-auto object-contain drop-shadow-sm',
                    isEditable && 'hover:opacity-70 transition-opacity cursor-pointer',
                  )}
                />
              ) : (
                <img
                  src="/img-placeholder-16-9.png"
                  alt="Logo Placeholder"
                  className={cn(
                    'h-20 w-20 object-cover rounded drop-shadow-sm opacity-60',
                    isEditable && 'hover:opacity-80 cursor-pointer transition-opacity',
                  )}
                />
              )}
              {isEditable && (
                <span className="absolute -bottom-5 left-0 text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  Change logo
                </span>
              )}
            </button>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(file, setAgencyLogoOverride, 'logos');
              }}
            />

            {/* Cover settings popover */}
            {isEditable && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-black/10 hover:bg-black/20 text-gray-700 transition-colors border border-black/10"
                    title="Cover settings"
                  >
                    <Pen className="w-3.5 h-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-64 p-3 flex flex-col gap-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Cover Settings</div>
                  
                  <div className="flex flex-col gap-2">
                    <Label className="text-xs text-muted-foreground">Background Type</Label>
                    <Tabs value={coverBgType} onValueChange={(v) => { setCoverBgType(v as 'solid' | 'gradient'); markDirty(); }}>
                      <TabsList className="grid w-full grid-cols-2 h-8">
                        <TabsTrigger value="solid" className="text-xs">Solid</TabsTrigger>
                        <TabsTrigger value="gradient" className="text-xs">Gradient</TabsTrigger>
                      </TabsList>
                    </Tabs>

                    {coverBgType === 'solid' ? (
                      <div className="flex items-center gap-2 mt-1">
                        <input 
                          type="color" 
                          value={coverColor} 
                          onChange={(e) => { setCoverColor(e.target.value); markDirty(); }}
                          className="w-8 h-8 rounded cursor-pointer border-0 p-0"
                        />
                        <span className="text-sm font-mono text-muted-foreground uppercase">{coverColor}</span>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <GradientEditor 
                          settings={coverGradientSettings} 
                          onChange={(settings, cssString) => {
                            setCoverGradientSettings(settings);
                            setCoverGradient(cssString);
                            markDirty();
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1.5 pt-2 border-t border-border">
                    <Label className="text-xs text-muted-foreground">Cover Photo (Overlay)</Label>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-1.5 h-8 text-xs"
                        onClick={() => coverInputRef.current?.click()}
                      >
                        <ImageIcon className="w-3.5 h-3.5" />
                        {coverPhoto ? 'Change' : 'Upload'}
                      </Button>
                      {coverPhoto && (
                        <Button
                          variant="outline"
                          size="icon"
                          className="w-8 h-8 text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                          onClick={() => { setCoverPhoto(''); markDirty(); }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                </PopoverContent>
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file, setCoverPhoto, 'covers');
                  }}
                />
              </Popover>
            )}
          </div>

          {/* Main content: title area — vertically centered */}
          <div className="relative z-10 flex flex-col justify-center px-16 sm:px-20 pt-24 pb-16" style={{ minHeight: '55%' }}>
            {/* Editable title */}
            {isEditable ? (
              <div 
                className="relative w-full group/title"
                ref={titleWrapperRef}
                onFocus={() => setShowTitleToolbar(true)}
                onClick={() => setShowTitleToolbar(true)}
              >
                <TextToolbar
                  isVisible={showTitleToolbar}
                  position={titleToolbarPos}
                  textStyles={titleTextStyles}
                  onStyleChange={handleTitleStyleChange}
                  isGlobalDarkMode={false}
                  elementType="h1"
                  onElementTypeChange={() => {}}
                  headingTypes={proposalHeadingTypes}
                />

                <textarea
                  ref={titleTextareaRef}
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="PROJECT&#10;PROPOSAL"
                  rows={1}
                  className="bg-transparent border-0 focus:outline-none focus:ring-0 resize-none leading-none placeholder:text-gray-400/60 w-full drop-shadow-sm overflow-hidden"
                  style={{
                    fontFamily: titleTextStyles.fontFamily,
                    fontSize: `${titleTextStyles.fontSize}px`,
                    letterSpacing: titleTextStyles.letterSpacing,
                    textTransform: titleTextStyles.textTransform !== 'none' ? titleTextStyles.textTransform as any : undefined,
                    color: titleTextStyles.color,
                    fontWeight: titleSettings.isBold ? 900 : 400,
                    fontStyle: titleTextStyles.fontStyle,
                    textDecoration: titleTextStyles.textDecoration,
                    textAlign: titleTextStyles.textAlign as any,
                    lineHeight: titleTextStyles.lineHeight,
                  }}
                />
              </div>
            ) : (
              <h1
                className="leading-none drop-shadow-sm whitespace-pre-wrap"
                style={{
                  fontFamily: titleTextStyles.fontFamily,
                  fontSize: `${titleTextStyles.fontSize}px`,
                  letterSpacing: titleTextStyles.letterSpacing,
                  textTransform: titleTextStyles.textTransform !== 'none' ? titleTextStyles.textTransform as any : undefined,
                  color: titleTextStyles.color,
                  fontWeight: titleSettings.isBold ? 900 : 400,
                  fontStyle: titleTextStyles.fontStyle,
                  textDecoration: titleTextStyles.textDecoration,
                  textAlign: titleTextStyles.textAlign as any,
                  lineHeight: titleTextStyles.lineHeight,
                }}
              >
                {title || 'PROJECT PROPOSAL'}
              </h1>
            )}
          </div>

          {/* Bottom footer bar */}
          <div
            className="absolute bottom-16 left-16 right-16 sm:bottom-20 sm:left-20 sm:right-20 z-10 p-8 rounded-xl"
            style={{
              background: footerBgType === 'gradient' ? footerGradient : footerBgColor,
              opacity: footerOpacity,
            }}
          >
            {/* Footer settings icon */}
            {isEditable && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20 text-gray-700 transition-colors"
                    title="Footer settings"
                  >
                    <Settings2 className="w-3.5 h-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-72 p-3 flex flex-col gap-3 max-h-[80vh] overflow-y-auto">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Footer Settings</div>

                  {/* Background Type */}
                  <div className="flex flex-col gap-2">
                    <Label className="text-xs text-muted-foreground">Background Type</Label>
                    <Tabs value={footerBgType} onValueChange={(v) => { setFooterBgType(v as 'solid' | 'gradient'); markDirty(); }}>
                      <TabsList className="grid w-full grid-cols-2 h-8">
                        <TabsTrigger value="solid" className="text-xs">Solid</TabsTrigger>
                        <TabsTrigger value="gradient" className="text-xs">Gradient</TabsTrigger>
                      </TabsList>
                    </Tabs>
                    {footerBgType === 'solid' ? (
                      <div className="flex items-center gap-2 mt-1">
                        <input type="color" value={footerBgColor} onChange={(e) => { setFooterBgColor(e.target.value); markDirty(); }} className="w-8 h-8 rounded cursor-pointer border-0 p-0" />
                        <span className="text-sm font-mono text-muted-foreground uppercase">{footerBgColor}</span>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <GradientEditor
                          settings={footerGradientSettings}
                          onChange={(settings, cssString) => {
                            setFooterGradientSettings(settings);
                            setFooterGradient(cssString);
                            markDirty();
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Opacity */}
                  <div className="flex flex-col gap-1.5 pt-2 border-t border-border">
                    <Label className="text-[10px] uppercase text-muted-foreground flex justify-between">
                      <span>Opacity</span><span>{Math.round(footerOpacity * 100)}%</span>
                    </Label>
                    <input type="range" min={0} max={1} step={0.01} value={footerOpacity}
                      onChange={(e) => { setFooterOpacity(Number(e.target.value)); markDirty(); }}
                      className="w-full h-2 cursor-pointer" />
                  </div>

                  {/* Title style */}
                  <div className="flex flex-col gap-2 pt-2 border-t border-border">
                    <Label className="text-xs text-muted-foreground">Title Style</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={footerStyleSettings.titleColor}
                        onChange={(e) => { setFooterStyleSettings(p => ({ ...p, titleColor: e.target.value })); markDirty(); }}
                        className="w-7 h-7 rounded cursor-pointer border-0 p-0" />
                      <span className="text-[10px] text-muted-foreground">Color</span>
                      <input type="number" min={10} max={60} value={footerStyleSettings.titleSize}
                        onChange={(e) => { setFooterStyleSettings(p => ({ ...p, titleSize: Number(e.target.value) })); markDirty(); }}
                        className="w-14 h-7 text-xs border border-border rounded px-1 text-center bg-transparent" />
                      <span className="text-[10px] text-muted-foreground">px</span>
                    </div>
                  </div>

                  {/* Text style */}
                  <div className="flex flex-col gap-2">
                    <Label className="text-xs text-muted-foreground">Content Text Style</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={footerStyleSettings.textColor}
                        onChange={(e) => { setFooterStyleSettings(p => ({ ...p, textColor: e.target.value })); markDirty(); }}
                        className="w-7 h-7 rounded cursor-pointer border-0 p-0" />
                      <span className="text-[10px] text-muted-foreground">Color</span>
                      <input type="number" min={8} max={32} value={footerStyleSettings.textSize}
                        onChange={(e) => { setFooterStyleSettings(p => ({ ...p, textSize: Number(e.target.value) })); markDirty(); }}
                        className="w-14 h-7 text-xs border border-border rounded px-1 text-center bg-transparent" />
                      <span className="text-[10px] text-muted-foreground">px</span>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}

            <div className="grid grid-cols-2 gap-12 relative text-left min-h-[140px]">
              {/* Vertical Divider */}
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-black/10 -translate-x-1/2" />

              <div className="flex flex-col justify-between">
                <div>
                  <FooterVariableTextarea
                    value={footerSettings.leftTitle}
                    onChange={(v) => { setFooterSettings(prev => ({ ...prev, leftTitle: v })); markDirty(); }}
                    rows={1}
                    style={{ fontWeight: 'bold', fontSize: `${footerStyleSettings.titleSize}px`, color: footerStyleSettings.titleColor, lineHeight: 1.2 }}
                  />
                </div>
                <div>
                  <FooterVariableTextarea
                    value={footerSettings.leftContent}
                    onChange={(v) => { setFooterSettings(prev => ({ ...prev, leftContent: v })); markDirty(); }}
                    rows={2}
                    style={{ fontSize: `${footerStyleSettings.textSize}px`, color: footerStyleSettings.textColor }}
                  />
                </div>
              </div>

              <div className="flex flex-col justify-between pl-4">
                <div>
                  <FooterVariableTextarea
                    value={footerSettings.rightTitle}
                    onChange={(v) => { setFooterSettings(prev => ({ ...prev, rightTitle: v })); markDirty(); }}
                    rows={1}
                    style={{ fontWeight: 'bold', fontSize: `${footerStyleSettings.titleSize}px`, color: footerStyleSettings.titleColor, lineHeight: 1.2 }}
                  />
                </div>
                <div>
                  <FooterVariableTextarea
                    value={footerSettings.rightContent}
                    onChange={(v) => { setFooterSettings(prev => ({ ...prev, rightContent: v })); markDirty(); }}
                    rows={2}
                    style={{ fontSize: `${footerStyleSettings.textSize}px`, color: footerStyleSettings.textColor }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Block editor below cover */}
        <div className="w-full" style={{ maxWidth: '960px' }}>
          <div className="bg-white rounded-none shadow-sm overflow-hidden border border-gray-100" style={{ borderRadius: '4px' }}>
            <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">Proposal Body</span>
              <MergeTagsHelper />
            </div>
            <div className="min-h-[500px]">
              <ProposalBlockEditor
                content={content}
                onChange={(c) => { setContent(c); markDirty(); }}
                editable={isEditable}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Send to Client dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" /> Send to Client
            </DialogTitle>
            <DialogDescription>
              Select the client and set when this offer expires. The proposal will be locked for editing once sent.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-1">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Client *</Label>
              <Select value={clientId} onValueChange={handleClientSelect}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select a client..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      <div className="flex flex-col text-left">
                        <span className="font-medium">{c.name || c.email}</span>
                        {c.name && <span className="text-xs text-muted-foreground">{c.email}</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Company <span className="normal-case">(for cover & merge tags)</span></Label>
              <Input
                value={companyName}
                onChange={(e) => { setCompanyName(e.target.value); markDirty(); }}
                className="mt-1.5"
                placeholder="Acme Inc."
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Offer valid until *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn('mt-1.5 w-full justify-start gap-2 font-normal', !validUntil && 'text-muted-foreground')}
                  >
                    <CalendarIcon className="w-4 h-4" />
                    {validUntil ? format(validUntil, 'PPP') : 'Pick an expiry date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={validUntil}
                    onSelect={(d) => { setValidUntil(d); markDirty(); }}
                    disabled={(d) => d < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="pt-1 border-t border-border text-xs text-muted-foreground space-y-1.5">
              <div className="flex justify-between">
                <span>Recipient</span>
                <span className="font-medium text-foreground">{clientName || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span>Email</span>
                <span className="font-medium text-foreground">{clientEmail || '—'}</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSend}
              disabled={!clientId || !validUntil}
              className="gap-2 text-white"
              style={{ backgroundColor: accentColor }}
            >
              <Send className="w-4 h-4" /> Send Proposal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save as template dialog */}
      <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save as workspace template</DialogTitle>
            <DialogDescription>Reuse this proposal's content for future clients.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Template name</Label>
            <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="e.g. Website Redesign Proposal" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} disabled={!templateName.trim()}>Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MergeTagsHelper: React.FC = () => {
  const [copiedTag, setCopiedTag] = React.useState<string | null>(null);

  const copyTag = (tag: string) => {
    navigator.clipboard.writeText(tag).catch(() => {});
    setCopiedTag(tag);
    setTimeout(() => setCopiedTag(null), 1500);
  };

  const categories = Array.from(new Set(MERGE_TAG_DEFS.map(t => t.category)));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="text-xs h-7 text-gray-400 hover:text-foreground">
          Variables
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 max-h-[480px] overflow-y-auto">
        <div className="text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wider">Available Variables</div>
        <p className="text-xs text-muted-foreground mb-3">
          Type <code className="bg-muted px-1 rounded">[</code> anywhere — in the footer or in the proposal body — to get autocomplete suggestions.
        </p>
        <div className="flex flex-col gap-3">
          {categories.map(cat => (
            <div key={cat}>
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">{cat}</div>
              <div className="space-y-1">
                {MERGE_TAG_DEFS.filter(t => t.category === cat).map(t => (
                  <div key={t.tag} className="flex items-center justify-between gap-2 group px-2 py-1.5 rounded hover:bg-muted/50">
                    <div>
                      <span className="font-mono text-xs text-foreground">{t.tag}</span>
                      <span className="text-[10px] text-muted-foreground ml-2">{t.label}</span>
                    </div>
                    <button
                      className="shrink-0 w-6 h-6 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:bg-muted transition-all"
                      onClick={() => copyTag(t.tag)}
                      title="Copy"
                    >
                      {copiedTag === t.tag
                        ? <Check className="w-3 h-3 text-green-500" />
                        : <Copy className="w-3 h-3 text-muted-foreground" />
                      }
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
